import React, { useState } from "react";
import { useNavigate, useLocation, Link } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { FirstAid } from "@phosphor-icons/react";

const BG = "https://customer-assets.emergentagent.com/job_bedstuy-ems-hub/artifacts/btxq2n5u_images.jpeg";
const LOGO_BSVAC = "https://customer-assets.emergentagent.com/job_bedstuy-ems-hub/artifacts/rcm1qmbk_BSVAC%20Patch.png";
const LOGO_CBVAC = "https://customer-assets.emergentagent.com/job_bedstuy-ems-hub/artifacts/j7fw33f8_imageedit_3_7066017002.png";

export default function Login() {
  const { login } = useAuth();
  // iter35 — Public registration removed. The login screen only
  // accepts existing accounts; new members are added by an admin.
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);
  const nav = useNavigate();
  const loc = useLocation();

  async function onSubmit(e) {
    e.preventDefault();
    setBusy(true);
    const res = await login(email, password);
    setBusy(false);
    if (res.ok) {
      toast.success("Welcome back");
      nav(loc.state?.from?.pathname || "/", { replace: true });
    } else {
      toast.error(res.error);
    }
  }

  return (
    <div className="min-h-screen flex" data-testid="login-page">
      <div className="hidden md:flex flex-1 relative items-end overflow-hidden">
        <img src={BG} alt="BSVAC ambulance fleet at Grand Army Plaza" className="absolute inset-0 w-full h-full object-cover" />
        <div className="absolute inset-0 bg-gradient-to-t from-slate-950/95 via-slate-950/75 to-slate-900/55" />
        <div className="relative p-10 text-white max-w-xl w-full">
          <div className="flex items-center gap-5 mb-8">
            <div className="h-24 w-24 flex items-center justify-center drop-shadow-[0_8px_24px_rgba(0,0,0,0.7)]">
              <img src={LOGO_BSVAC} alt="BSVAC patch" className="max-h-full max-w-full object-contain" data-testid="logo-bsvac" />
            </div>
            <div className="h-24 w-24 flex items-center justify-center drop-shadow-[0_8px_24px_rgba(0,0,0,0.7)]">
              <img src={LOGO_CBVAC} alt="Central Brooklyn Volunteer Ambulance Corps seal" className="max-h-full max-w-full object-contain" data-testid="logo-cbvac" />
            </div>
            <div className="flex flex-col pl-3 border-l border-white/20">
              <span className="tab-tag text-slate-300">EST. 1988 · BEDFORD-STUYVESANT, NY</span>
              <span className="font-display font-black tracking-tight text-base">OPERATIONS CONSOLE</span>
            </div>
          </div>
          <h2 className="font-display text-2xl lg:text-3xl font-black tracking-tight leading-tight" data-testid="login-org-line-bsvac">
            Bedford Stuyvesant Volunteer Ambulance Corps
          </h2>
          <h2 className="font-display text-2xl lg:text-3xl font-black tracking-tight leading-tight text-slate-100 mt-2" data-testid="login-org-line-cbvac">
            Central Brooklyn Volunteer Ambulance Corps
          </h2>
          <p className="text-slate-300 mt-5 max-w-md leading-relaxed">
            Internal operations console — scheduling, logistics, documentation and supervisory functions for EMS volunteers.
          </p>
          <div className="mt-10 border-t border-white/15 pt-6 grid grid-cols-2 gap-x-6 gap-y-3 text-xs text-slate-200">
            <div><div className="tab-tag text-slate-400">Authorized</div> Members Only</div>
            <div><div className="tab-tag text-slate-400">Region</div> Bedford-Stuyvesant, NY</div>
            <div><div className="tab-tag text-slate-400">Year Est.</div> 1988</div>
          </div>
        </div>
      </div>

      <div className="flex-1 flex items-center justify-center bg-white px-6 py-10">
        <div className="w-full max-w-sm">
          <div className="flex items-center gap-3 mb-8 md:hidden">
            <img src={LOGO_BSVAC} alt="BSVAC" className="h-12 w-12 object-contain" />
            <img src={LOGO_CBVAC} alt="CBVAC" className="h-12 w-12 object-contain" />
            <div className="leading-tight">
              <div className="font-display font-black tracking-tight text-sm">BSVAC / CBVAC</div>
              <div className="tab-tag text-slate-500">Operations Console</div>
            </div>
          </div>

          <div className="tab-tag text-slate-500 mb-1">Restricted Access</div>
          <h1 className="font-display text-3xl font-black tracking-tighter text-slate-900">
            Sign in to console
          </h1>
          <p className="text-sm text-slate-500 mt-1">
            Use your organizational credentials.
          </p>

          <form onSubmit={onSubmit} className="mt-6 space-y-4">
            <div>
              <Label htmlFor="email" className="tab-tag">Email or Username</Label>
              <Input
                id="email"
                data-testid="login-email"
                type="text"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="rounded-sm text-base h-11"
                autoComplete="username"
                autoCapitalize="none"
                autoCorrect="off"
                spellCheck={false}
                inputMode="text"
                enterKeyHint="next"
              />
            </div>
            <div>
              <Label htmlFor="password" className="tab-tag">Password</Label>
              <Input
                id="password"
                data-testid="login-password"
                type="password"
                required
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="rounded-sm text-base h-11"
                autoComplete="current-password"
                autoCapitalize="none"
                autoCorrect="off"
                spellCheck={false}
                enterKeyHint="go"
              />
            </div>
            <Button type="submit" disabled={busy} data-testid="submit-auth" className="w-full rounded-sm bg-slate-900 hover:bg-slate-800 text-white font-semibold h-11 text-base">
              {busy ? "Please wait…" : "Sign in"}
            </Button>
          </form>

          {/* iter35 — Public self-registration removed per BSVAC policy.
              All member accounts are created by an administrator via the
              Admin → Users workflow (or ingested from a membership
              application), so the public "Create account" switcher has
              been dropped from the login screen. */}
        </div>
      </div>
    </div>
  );
}
